<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title>Language Tests</v-toolbar-title>
      <v-spacer />
      <v-dialog v-model="addDialog" width="600">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on">
            <v-icon left>fal fa-plus</v-icon>
            Add Test
          </v-btn>
        </template>
        <v-card>
          <v-toolbar class="mb-2">
            <v-toolbar-title>Add Language Test</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <p>Please add the name and email of the person who will be taking the test. If they have a Banner ID, you can enter that as well. If they don't have one or if you don't know it, then you can leave it blank. This isn't necessary but allows the scores to be automatically synced with Banner and Slate upon completion.</p>
            <v-row>
              <v-col>
                <v-text-field v-model="addFirst" label="First Name" outlined hide-details />
              </v-col>
              <v-col>
                <v-text-field v-model="addLast" label="Last Name" outlined hide-details />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field v-model="addEmail" label="Email Address" outlined hide-details />
              </v-col>
              <v-col>
                <v-text-field v-model="addBannerId" label="Banner ID" outlined hide-details />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select v-model="addAssessment" :items="assessments" label="Assessment to Assign" outlined hide-details />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn :disabled="addNotReady" color="success" text @click="addTest">Add Test</v-btn>
            <v-spacer />
            <v-btn text @click="addDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-card-text>
      <p>This page holds links between Scots Connect, Emmersion, Banner, and CBI Pre-registration.</p>
      <v-row>
        <v-col>
          <v-text-field v-model="search" label="Table Search" prepend-icon-inner="fal fa-search" outlined hide-details />
        </v-col>
        <v-col>
          <v-select v-model="statusFilter" :items="statusOptions" label="Status Filter" multiple chips small-chips deletable-chips outlined hide-details />
        </v-col>
        <v-col>
          <v-select v-model="languageFilter" :items="languageOptions" label="Assessment Filter" multiple chips small-chips deletable-chips outlined hide-details />
        </v-col>
        <v-col>
          <v-select v-model="placementFilter" :items="placementOptions" label="Placement Level" multiple chips small-chips deletable-chips outlined hide-details />
        </v-col>
      </v-row>
    </v-card-text>
    <v-data-table :items="filteredList" :headers="headers" :search="search">
      <template v-slot:item.placement="{ item }">
        <v-dialog v-if="item.review && 'newPlacement' in item.review && item.review.newPlacement && 'origPlacement' in item.review && item.review.newPlacement !== item.review.origPlacement" width="400">
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" outlined>
              <v-icon left :color="parseInt(item.review.newPlacement) > parseInt(item.review.origPlacement) ? 'success' : 'error'">fas fa-arrow-circle-{{ parseInt(item.review.newPlacement) > parseInt(item.review.origPlacement) ? 'up' : 'down' }}</v-icon>
              {{ item.review.newPlacement }}
            </v-btn>
          </template>
          <v-card>
            <v-card-title>Placement Updated</v-card-title>
            <v-card-text>
              <p>After review, the placement level was updated from {{ item.review.origPlacement }} to {{ item.review.newPlacement }}.</p>
            </v-card-text>
          </v-card>
        </v-dialog>
        <span v-else>{{ item.placement }}</span>
      </template>
      <template v-slot:item.uploadLink="{ item }">
        <v-icon v-if="item.uploadLink || (item.action && item.action.linkId)" color="success">fas fa-check</v-icon>
        <v-tooltip v-else-if="item.action" top>
          <template v-slot:activator="{ on }">
            <v-icon v-if="item.action.done" v-on="on" color="error">fas fa-times</v-icon>
            <v-icon v-else v-on="on" color="info">fas fa-exclamation</v-icon>
          </template>
          <span v-if="item.action.done">The student has dismissed this action item so they cannot currently complete the speech upload</span>
          <span v-else>The speech upload is pending on the student's to-do list in Scots Connect</span>
        </v-tooltip>
      </template>
      <template v-slot:item.review="{ item }">
        <v-icon v-if="item.review" color="success">fas fa-check</v-icon>
        <v-btn v-else @click="reviewPlacement(item)">
          <v-icon>fal fa-eye</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <v-dialog v-model="reviewDialog" width="400">
      <v-alert v-if="loading">Loading...</v-alert>
      <v-card v-else>
        <v-toolbar class="mb-4">Review for {{ selected.first + ' ' + selected.last }}</v-toolbar>
        <v-card-text>
          <v-text-field :value="selected.language" label="Assessment Taken" readonly />
          <v-row>
            <v-col>
              <v-text-field :value="selected.score" label="Score Achieved" readonly />
            </v-col>
            <v-col>
              <v-text-field :value="selected.placement" label="Placement" readonly />
            </v-col>
          </v-row>
          <v-btn v-if="audioIsLoading" disabled>Loading audio clip...</v-btn>
          <v-btn v-else-if="isPlaying" @click="stopAudio">Stop Audio Clip</v-btn>
          <v-btn v-else-if="selected.uploadLink" @click="playAudio">Play Audio Clip</v-btn>
          <v-alert v-else-if="selected.action && !selected.action.done" type="info" outlined colored-border border="left">Upload action is pending in student's to-do list</v-alert>
          <v-text-field v-model="newPlacement" label="Updated Placement" hint="Leave blank to not change" persistent-hint outlined class="mt-2" />
          <v-btn v-if="selected.actionIndex == null || (selected.action && selected.action.done)" color="error" @click="addSpeechUpload()">{{ selected.action && selected.action.done ? 'Request New Speech Upload' : 'Add Speech Upload to To-Do List' }}</v-btn>
        </v-card-text>
        <v-card-actions>
          <v-btn color="success" @click="submitReview">Mark as Reviewed</v-btn>
          <v-spacer />
          <v-btn @click="reviewDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<style>
.v-input--is-readonly .v-input__slot::before {
  border-color: #CCC !important;
}
</style>
<script>
import { computed, onMounted, ref, watch } from '@vue/composition-api'
import { stringFormatDate } from '@/helpers/formatters'

export default {
  setup (props, { root }) {
    const service = root.$feathers.service('orientation/language-placement')
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const statusFilter = ref([])
    const statusOptions = ref([
      { text: 'Unsubmitted Test', value: 'noscore' },
      { text: 'Submission Pending', value: 'pendingSubmit' },
      { text: 'Dismissed, Unsubmitted', value: 'dismissed' },
      { text: 'Review Pending', value: 'pendingReview' },
      { text: 'Review Completed', value: 'complete' },
      { text: 'Review Not Needed', value: 'placement-100' }
    ])
    const languageFilter = ref([])
    const languageOptions = ref(['French'])
    const placementFilter = ref([])
    const placementOptions = ref([
      { text: '101-level', value: '101' },
      { text: '102-level', value: '102' },
      { text: '201-level', value: '201' },
      { text: '202-level', value: '202' },
      { text: '300-level', value: '300' }
    ])
    const search = ref('')
    const term = ref('')
    const list = ref([])
    const filteredList = computed(() => list.value.filter(({ language }) => languageFilter.value.length === 0 || languageFilter.value.includes(language))
      .filter(({ placement }) => placementFilter.value.length === 0 || (placement && placementFilter.value.includes(placement.replaceAll(/[^0-9]/g, ''))))
      .filter(({ score, placement, review, uploadLink, action }) => {
        if (statusFilter.value.length === 0) return true
        if (statusFilter.value.includes('noscore') && score == null) return true
        else if (score == null) return false
        // We should always have a placement because anything above would have cleared out any without a placement
        const placementNum = parseInt(placement.replaceAll(/[^0-9]/g, ''))
        if (statusFilter.value.includes('placement-100') && placementNum < 200) return true
        if (statusFilter.value.includes('pendingReview') && !review && placementNum >= 200 && uploadLink) return true
        if (statusFilter.value.includes('pendingSubmit') && !review && !uploadLink && action && !action.done) return true
        if (statusFilter.value.includes('dismissed') && !review && !uploadLink && action && action.done) return true
        if (statusFilter.value.includes('complete') && review) return true
        return false
      })
    )
    const headers = ref([
      { text: 'Source', value: 'source' },
      { text: 'Banner ID', value: 'bannerId' },
      { text: 'Last Name', value: 'last' },
      { text: 'First Name', value: 'first' },
      { text: 'Email Address', value: 'email' },
      { text: 'Assessment', value: 'language' },
      { text: 'Placement', value: 'placement' },
      { text: 'Submitted Audio', value: 'uploadLink' },
      { text: 'Review Placement', value: 'review' }
    ])
    const assessments = ref([])

    onMounted(async () => {
      const dt = new Date()
      const year = dt.getFullYear()
      term.value = year + (dt.getMonth() < 7 ? '05' : '60')
      const aggregate = [
        { $match: { term: term.value } },
        { $lookup: {
          from: 'Orientation-Tracker',
          as: 'tracker',
          let: { pidm: '$pidm', term: '$term' },
          pipeline: [
            { $match: { $and: [{ $expr: { $eq: ['$pidm', '$$pidm'] } }, { $expr: { $eq: ['$term', '$$term'] } }] } },
            { $unwind: { path: '$actions', includeArrayIndex: 'actionIndex', preserveNullAndEmptyArrays: false } },
            { $match: { 'actions.link': '/action/language' } },
            { $project: { index: '$actionIndex', action: '$actions' } }
          ]
        } },
        { $addFields: {
          tracker: { $last: '$tracker' }
        } },
        { $addFields: {
          action: '$tracker.action',
          actionIndex: '$tracker.index',
          trackerId: '$tracker._id'
        } },
        { $sort: { createdAt: 1 } }
      ]
      const { data } = await service.find({ query: { aggregate } })
      for (const { language } of data) {
        if (!languageOptions.value.includes(language)) languageOptions.value.push(language)
      }
      list.value = data
      audioContext.value = new AudioContext()
      const { data: assessmentData } = await service.find({ query: { report: 'assessments' } })
      assessments.value = assessmentData.map(({ name: text, assessmentId: value }) => { return { text, value } })
    })

    const loading = ref(false)
    const reviewDialog = ref(false)
    const selected = ref({
      _id: '',
      pidm: '',
      bannerId: '',
      first: '',
      last: '',
      language: '',
      score: '',
      placement: '',
      uploadLink: '',
      actionIndex: null,
      action: {
        done: false
      }
    })
    const audioClip = ref(null)
    const newPlacement = ref('')
    async function reviewPlacement (item) {
      reviewDialog.value = true
      selected.value = item
    }
    const audioContext = ref(null)
    const audioSource = ref(null)
    const audioBuffer = ref(null)
    const audioIsLoading = ref(false)
    const isPlaying = ref(false)
    async function playAudio () {
      if (audioBuffer.value == null) {
        try {
          audioIsLoading.value = true
          const { data: binaryData } = await root.$feathers.service('service/drive-upload').get(selected.value.uploadLink, { query: { download: 'Y' } })
          audioBuffer.value = await audioContext.value.decodeAudioData(binaryData)
          audioIsLoading.value = false
        } catch (error) {
          alert('Error decoding or playing audio:', error)
        }
      }
      audioSource.value = audioContext.value.createBufferSource()
      audioSource.value.onended = () => {
        isPlaying.value = false
      }
      audioSource.value.buffer = audioBuffer.value
      audioSource.value.connect(audioContext.value.destination)
      audioIsLoading.value = false
      audioSource.value.start(0)
      isPlaying.value = true
    }
    async function stopAudio () {
      isPlaying.value = false
      audioSource.value.stop()
    }
    async function submitReview () {
      const review = {
        date: new Date(),
        user: user.value.username,
        origPlacement: selected.value.placement,
        newPlacement: newPlacement.value
      }
      const rec = await service.patch(selected.value._id, { review })
      const index = list.value.findIndex(({ _id }) => _id === selected.value._id)
      if (index >= 0) {
        list.value.splice(index, 1, rec)
      }
      reviewDialog.value = false
    }
    watch(reviewDialog, async () => {
      if (!reviewDialog.value && audioSource.value != null) {
        await audioContext.value.close()
        audioSource.value = null
      }
    })

    const addDialog = ref(false)
    const addFirst = ref('')
    const addLast = ref('')
    const addEmail = ref('')
    const addBannerId = ref('')
    const addAssessment = ref('')

    const addNotReady = computed(() => addFirst.value === '' || addLast.value === '' || addEmail.value === '' || (addBannerId.value !== '' && !/^@[0-9]{8}$/.test(addBannerId.value)) || addAssessment.value === '')

    async function addTest () {
      const rec = assessments.value.find(({ value }) => value === addAssessment.value)
      const language = rec ? rec.text : ''
      const obj = {
        term: term.value,
        assessmentId: addAssessment.value,
        first: addFirst.value,
        last: addLast.value,
        email: addEmail.value,
        bannerId: addBannerId.value,
        language,
        source: 'Portal'
      }
      console.log(obj)
      try {
        const res = await service.create(obj)
        const { email, singleAssessmentSignInUrl: link, singleAssessmentSignInUrlExpiresAt: expireDate } = res
        await root.$feathers.service('system/email').create({ to: email, from: 'report-email@covenant.edu', subject: language + ' Placement Test', html: `You have just been assigned the ${language} placement test. Use the link below to access the placement test. The link will expire on ${stringFormatDate(expireDate)}, so make sure to complete the test prior to that date.<br/><br/><a href="${link}">Take Assessment</a>` })
        root.$store.dispatch('main/snackbar', { color: 'success', text: 'Assessment email has been sent' })
      } catch (e) {
        root.$store.dispatch('main/snackbar', { color: 'error', text: 'Error sending assessment: ' + e })
      }
      addDialog.value = false
    }

    async function addSpeechUpload () {
      let { _id, language, uploadLink, trackerId, actionIndex, pidm, term } = selected.value
      if (!trackerId) {
        // They don't have a tracker ID because it only loads the tracker ID if they have the /action/language option in their upload already
        const { data } = await root.$feathers.service('orientation/tracker').find({ query: { pidm, term } })
        if (data.length > 0) {
          trackerId = data[0]._id
        }
      }
      const action = { link: '/action/language', title: 'Upload Speech Sample for ' + language, added: new Date() }
      if (actionIndex && actionIndex >= 0) {
        try {
          // If the student submitted audio (i.e.d uploadLink is not null) then patch the language-placement to set it to null
          if (uploadLink) await service.patch(_id, { uploadLink: null })
          // Patch the tracker for this given action index to add it back for the student to submit it again
          await root.$feathers.service('orientation/tracker').patch(trackerId, { ['actions.' + actionIndex]: action })
          root.$store.dispatch('main/snackbar', { color: 'success', text: `Reactivated "Upload Speech Sample for ${language}" to student's to-do list` })
        } catch (e) {
          root.$store.dispatch('main/snackbar', { color: 'error', text: 'Error adding speech upload: ' + e })
          return
        }
      } else {
        try {
          // Push the language speech upload to the actions list in the tracker
          const temp = await root.$feathers.service('orientation/tracker').patch(trackerId, { $push: { actions: action } })
          actionIndex = temp.actions.length - 1
          root.$store.dispatch('main/snackbar', { color: 'success', text: `Added "Upload Speech Sample for ${language}" to student's to-do list` })
        } catch (e) {
          root.$store.dispatch('main/snackbar', { color: 'error', text: 'Error adding speech upload: ' + e })
          return
        }
      }
      const index = list.value.findIndex(({ _id: i }) => i === _id)
      if (index >= 0) {
        list.value.splice(index, 1, { ...list.value[index], actionIndex, action })
      }
      reviewDialog.value = false
    }

    return {
      user,
      statusFilter,
      statusOptions,
      languageFilter,
      languageOptions,
      placementFilter,
      placementOptions,
      search,
      list,
      filteredList,
      headers,
      assessments,
      loading,
      reviewDialog,
      selected,
      audioClip,
      newPlacement,
      audioIsLoading,
      isPlaying,
      playAudio,
      stopAudio,
      reviewPlacement,
      submitReview,
      addDialog,
      addFirst,
      addLast,
      addEmail,
      addBannerId,
      addAssessment,
      addNotReady,
      addTest,
      addSpeechUpload
    }
  }
}
</script>
